import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import { useConstant } from '@wearejh/react-hooks/lib/useConstant';
import { debounceTime, skip, subscribeOn, tap } from 'rxjs/operators';
import { asyncScheduler, merge, Subject } from 'rxjs';

import { useHistoryLocationObservable } from 'src/hooks/router-hooks';
import type { jhMenu_jhMenu } from 'src/queries/__generated__/jhMenu';
import { HeaderContext } from 'src/components/Header/Header';

import { Svg } from '../Svg/Svg';
import { stripDomain } from '../../util/sanitizeLink';
import { useBreakpointMatch } from '../../hooks/useBreakpoint';

import { NavSub } from './NavSub';
import classes from './Nav.scss';

type NavListProps = {
    jhMenu: (jhMenu_jhMenu | null)[];
    setOverflow?: (value: boolean) => void;
};

export const NavList: React.FC<NavListProps> = React.memo(({ jhMenu, setOverflow }) => {
    const { setShowNav } = useContext(HeaderContext);
    const { isMob, isDesk } = useBreakpointMatch();
    const [openIndexes, toggleList] = useState(-1);

    const location$ = useHistoryLocationObservable();
    const hover$ = useConstant(() => new Subject<number>());
    const onMouseEnterOrLeave = useCallback(
        (v: number, hasChildren: boolean) => hasChildren && hover$.next(v),
        [hover$],
    );
    const navListRef = useRef<HTMLUListElement>(null);

    useEffect(() => {
        const locationChanges$ = location$.pipe(
            skip(1),
            subscribeOn(asyncScheduler),
            tap(() => toggleList(-1)),
            tap(() => {
                setShowNav(false);
                document.body.dataset.navActive = '';
            }),
        );
        const hovers$ = hover$.pipe(
            debounceTime(200),
            tap((x) => toggleList(x)),
            tap((x) => {
                if (x > -1) {
                    setShowNav(true);
                } else {
                    setShowNav(false);
                }
            }),
        );
        const sub = merge(locationChanges$, hovers$).subscribe();

        const handleFocusOut = (event: FocusEvent) => {
            if (isDesk && navListRef.current && !navListRef.current.contains(event.relatedTarget as Node)) {
                toggleList(-1);
                setShowNav(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.code === 'Escape' && isDesk) {
                toggleList(-1);
                setShowNav(false);
            }
        };

        const navList = navListRef.current;

        if (navList) {
            navList.addEventListener('keydown', (event) => handleKeyDown(event));
            navList.addEventListener('focusout', (event) => handleFocusOut(event));
        }

        return () => {
            sub.unsubscribe();
            if (navList) {
                navList.removeEventListener('keydown', handleKeyDown);
                navList.removeEventListener('focusout', handleFocusOut);
            }
        };
    }, [hover$, isDesk, jhMenu.length, location$, setShowNav]);

    /**
     * an attribute to be used with CSS to show desk menu
     */
    useEffect(() => {
        if (isDesk) {
            const elem = document.getElementById('nav-list');
            if (elem) {
                (elem.dataset as any).deskReady = true;
            }
        }
    }, [isDesk]);

    return (
        <>
            <h2 className={classes.navHeaderTitle}>Categories</h2>

            <ul
                className={classes.navList}
                id="nav-list"
                data-test-id="main-menu"
                data-accessibility="global"
                ref={navListRef}
            >
                {jhMenu.map((item, index) => {
                    if (!item) {
                        return null;
                    }

                    const hasChildren = item.children.length > 0;
                    const active = openIndexes === index;
                    const itemUrl = stripDomain(item.url);
                    const hideShopAll = Boolean(item.classes?.includes('hide-shop-all'));

                    return (
                        <li
                            key={item.node_id}
                            className={classnames({
                                [classes.navItem]: true,
                                [item.classes ?? '']: item.classes,
                            })}
                            onMouseEnter={() => {
                                if (isDesk) {
                                    onMouseEnterOrLeave(index, hasChildren);
                                }
                            }}
                            onMouseLeave={() => {
                                if (isDesk) {
                                    onMouseEnterOrLeave(-1, hasChildren);
                                }
                            }}
                            onBlur={(event: React.FocusEvent<HTMLLIElement>) => {
                                if (isDesk && active && hasChildren) {
                                    if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
                                        onMouseEnterOrLeave(-1, hasChildren);
                                    }
                                }
                            }}
                        >
                            <Link
                                to={itemUrl}
                                className={classnames({
                                    [classes.navLink]: true,
                                    [item.classes ?? '']: item.classes,
                                })}
                                onClick={(e) => {
                                    if (isMob && hasChildren) {
                                        e.preventDefault();
                                        const item = document.querySelector('[data-nav-top]');

                                        if (item) {
                                            item.scrollTo(0, 0);
                                        }

                                        toggleList(index);
                                        setOverflow?.(false);
                                    }
                                }}
                                onKeyDown={(event) => {
                                    if (event.code === 'Enter' || event.code === 'Space') {
                                        if (isDesk && hasChildren) {
                                            event.preventDefault();

                                            if (!active) {
                                                onMouseEnterOrLeave(index, hasChildren);
                                            } else {
                                                onMouseEnterOrLeave(-1, hasChildren);
                                            }
                                        }
                                    }
                                }}
                                aria-expanded={hasChildren ? active : undefined}
                                aria-label={`Go to ${item.title}`}
                            >
                                {item.title}
                                {hasChildren && <Svg name="arrow-right" aria-hidden="true" />}
                            </Link>
                            {hasChildren && (
                                <NavSub
                                    hideShopAll={hideShopAll}
                                    subNavs={item.children}
                                    parentUrl={itemUrl || ''}
                                    parentTitle={item.title || 'unknown'}
                                    showSub={active}
                                    toggleList={(value) => toggleList(value)}
                                    setParentOverflow={setOverflow}
                                    nodeId={item.node_id}
                                />
                            )}
                        </li>
                    );
                })}
            </ul>
        </>
    );
});
